import { useTranslation } from 'react-i18next'
import { hexToHsva, hsvaToHex } from '@uiw/color-convert'
import Alert from '@/shared/components/Alert/Alert'
import ColorPicker from '@/shared/components/ColorPicker/ColorPicker'
import IconButton from '@/shared/components/IconButton/IconButton'
import { DEFAULT_BRAND_COLOR } from '../../config'
import {
  PALETTE_ROLES,
  PaletteRole,
  contrastRatio,
  resolvePalette
} from '../../render/palette'
import { Theme } from '../../render/theme'
import styles from './PropertiesPanel.module.scss'

interface PageColorsProps {
  theme: Theme
  /** A finished decision: one step in the undo history. */
  onChange: (theme: Theme) => void
  /**
   * A value from a pointer still on the wheel. The page follows it, the history
   * does not: `onChange` alone would turn one drag into fifty undo steps.
   */
  onPreview: (theme: Theme) => void
  /** The pointer was released. Closes the burst `onPreview` opened. */
  onPreviewEnd: () => void
}

/** Below this the body text is failing WCAG AA and the owner is told so. */
const MIN_TEXT_CONTRAST = 4.5

/**
 * Every colour of the page, one picker each. The builder's side of the deal.
 *
 * Drawn without a heading of its own: it is one group inside the settings
 * panel, and the group's own header already names it.
 *
 * The template sets one brand colour and derives the rest; this is where
 * somebody who wants a specific colour for a specific thing sets exactly that.
 * A picker writes only its own role into `theme.colors`, never the brand
 * colour, so the two never overwrite each other. The undo arrow beside a
 * changed colour puts that one role back on the derivation.
 *
 * No colour is refused. The simple path guarantees readability by construction;
 * the advanced one warns instead, because the person here asked for control.
 */
function PageColors(props: PageColorsProps) {
  const { theme, onChange, onPreview, onPreviewEnd } = props
  const { t } = useTranslation()

  const palette = resolvePalette(
    theme.brandColor ?? DEFAULT_BRAND_COLOR,
    theme.colors
  )
  const overrides = theme.colors ?? {}

  const withRole = (role: PaletteRole, hex: string): Theme => ({
    ...theme,
    colors: { ...overrides, [role]: hex }
  })

  const resetRole = (role: PaletteRole) => {
    const rest = Object.fromEntries(
      Object.entries(overrides).filter(([key]) => key !== role)
    )
    onChange({ ...theme, colors: Object.keys(rest).length ? rest : undefined })
  }

  const textContrast = contrastRatio(palette.text, palette.bg)
  const buttonContrast = contrastRatio(palette.onPrimary, palette.primary)

  return (
    <div className={styles.panel}>
      <p className={`body-small ${styles.hint}`}>{t('WEBSITE.COLORS.HINT')}</p>

      {(textContrast < MIN_TEXT_CONTRAST ||
        buttonContrast < MIN_TEXT_CONTRAST) && (
        <Alert severity="warning" message={t('WEBSITE.COLORS.LOW_CONTRAST')} />
      )}

      {PALETTE_ROLES.map(role => (
        <div key={role} className={styles.colorRow}>
          <ColorPicker
            fullWidth
            label={t(`WEBSITE.COLORS.ROLE.${role}`)}
            color={hexToHsva(palette[role])}
            setColor={next => onPreview(withRole(role, hsvaToHex(next)))}
            onChangeCommitted={next => {
              onPreview(withRole(role, hsvaToHex(next)))
              onPreviewEnd()
            }}
          />
          {overrides[role] && (
            <IconButton
              icon="las la-undo icon-medium"
              color="primary"
              ariaLabel={t('WEBSITE.COLORS.RESET')}
              onClick={() => resetRole(role)}
            />
          )}
        </div>
      ))}
    </div>
  )
}

export default PageColors
